import React from 'react'

import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  FetchResult,
  fromPromise,
  NormalizedCacheObject,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'

import { useNavigation } from '@/context/navigation-context'
import { LOGOUT } from '@/graphql/auth/mutations'
import { UPDATE_TOKENS } from '@/graphql/public-pages/login/mutations'
import {
  devFABGraphqlUrl,
  devRobotGraphqlUrl,
  devSFDIGraphqlUrl,
  isProduction,
  prodFABGraphqlUrl,
  prodRobotGraphqlUrl,
  prodSFDIGraphqlUrl,
} from '@/helpers/config'
import { ClientName } from '@/models/auth'
import i18n from '@/translations'

import { cache, resetReactiveVariables } from './cache'

const refreshAuthToken = (client: ApolloClient<NormalizedCacheObject>): Promise<FetchResult> =>
  client.mutate({
    mutation: UPDATE_TOKENS,
  })
const logout = (client: ApolloClient<NormalizedCacheObject>): Promise<FetchResult> =>
  client.mutate({
    mutation: LOGOUT,
  })

const SFDILink = createHttpLink({
  uri: isProduction ? prodSFDIGraphqlUrl : devSFDIGraphqlUrl,
  credentials: 'include',
})

const FABLink = createHttpLink({
  uri: isProduction ? prodFABGraphqlUrl : devFABGraphqlUrl,
  credentials: 'include',
})

const ROBOTLink = createHttpLink({
  uri: isProduction ? prodRobotGraphqlUrl : devRobotGraphqlUrl,
  credentials: 'include',
})

const httpLink = split(
  operation => operation.getContext().clientName === ClientName.ROBOT,
  ROBOTLink,
  split(operation => operation.getContext().clientName === ClientName.FAB, FABLink, SFDILink),
)

const authLink = setContext((_, { headers, ...context }) => {
  return {
    headers: {
      ...headers,
      'Accept-Language': i18n.language,
    },
    clientName: ClientName.SFDI,
    ...context,
  }
})

const createErrorLink = setError =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      // User access token has expired
      if (graphQLErrors[0].extensions?.code === 'UNAUTHORISED') {
        // Refresh token through async request
        return fromPromise(
          refreshAuthToken(createApolloClient(setError))
            .then(async ({ data }) => {
              if (!data?.updateAccessTokensSA?.login) {
                return await logout(createApolloClient(setError)).then(() => {
                  setError('UNAUTHORISED')
                  createApolloClient(setError).clearStore()
                  resetReactiveVariables()
                })
              }
            })
            .catch(async () => {
              return await logout(createApolloClient(setError)).then(() => {
                setError('UNAUTHORISED')
                createApolloClient(setError).clearStore()
                resetReactiveVariables()
              })
            }),
        ).flatMap(() => forward(operation))
      }

      return graphQLErrors.forEach(({ message, locations, path: errorPath }) =>
        console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${errorPath}`),
      )
    }

    if (networkError) {
      console.log(`[Network error]: ${networkError}`)
    }
  })

const createApolloClient = setError => {
  return new ApolloClient({
    cache,
    link: ApolloLink.from([createErrorLink(setError), authLink.concat(httpLink)]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'network-only',
      },
    },
  })
}

export const ApolloProviderWithNavigation = ({ children }) => {
  const { setError } = useNavigation()

  return <ApolloProvider client={createApolloClient(setError)}>{children}</ApolloProvider>
}
